import { action } from 'mobx';
import { ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useOptionalMessage } from 'Hooks/useOptionalMessage';
import { appConfigStore } from 'Models/AppConfigStore';
import { getMessageByKey } from 'Stores/LanguageStore';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import If from 'Views/Components/If/If';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { RenderRemoveTrailerModal } from '../../Modals/RemoveTrailerModal';
import { PreviousCargo } from './PreviousCargo';
import { VehicleTabActionCreators, wizardDataVehicleTabReducer } from './Reducers/vehicleTabReducer';
import { TrailerExactWeightInput } from './TrailerExactWeightInput';
import { TrailerLengthInput } from './TrailerLengthInput';
import { TrailerTypeInput } from './TrailerTypeInput';
import VehicleCargoCharacteristicDetails from './VehicleCargoCharacteristicDetails';
import TrailerCargoCharacteristicDetails from './TrailerCargoCharacteristicDetails';
import TrailerWidthDetails from './TrailerWidthDetails';
import { VehicleDriverDetailsForm } from './VehicleDriverDetailsForm';
import DynamicFieldsSection from '../BookingDetails/DynamicFieldsSection';
import { useSubjectUserDisplayNameId } from '../BookingDetails/useSubjectUserDisplayNameId';
import { VehicleExactWeightInput } from './VehicleExactWeightInput';
import { VehicleLengthInput } from './VehicleLengthInput';
import { VehicleMakeInput } from './VehicleMakeInput';
import { VehicleModelInput } from './VehicleModelInput';
import { VehicleRegoInput } from './VehicleRegoInput';
import { useVehicleTabContext } from './VehicleTabContext';
import { useResetDisallowedMeasurements } from './Hooks/useResetDisallowedMeasurements';
import VehicleWidthDetails from './VehicleWidthDetails';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export function VehicleTabInner() {
	const intl = useIntl();
	const fmtOptional = useOptionalMessage();
	const subjectUserDisplayNameId = useSubjectUserDisplayNameId();

	const {
		cargoTypes,
		previousCargo,
		previousCargoList,
		setPreviousCargoList,
		refetchVehicleTabData,
		isGuest,
		isUserLoading: isLoading,
	} = useVehicleTabContext();

	const { wizardData, setWizardData, oldWizardData, innerWizardErrors, setInnerWizardErrors } =
		useBookingWizardDataStore(x => ({
			wizardData: x.wizardData as BookingWizardData<VehicleBookingWizardData>,
			setWizardData: x.setWizardData,
			oldWizardData: x.oldWizardData,
			innerWizardErrors: x.innerWizardErrors,
			setInnerWizardErrors: x.setInnerWizardErrors,
		}));

	// If the previously selected vehicle type is no longer available in the list, we want to de-select it and let the user know
	// that it is no longer available to be booked.
	useEffect(() => {
		if (wizardData.innerWizardData instanceof VehicleBookingWizardData && !wizardData.disabledVehicleType) {
			if (
				isNotNullOrUndefined(wizardData.innerWizardData.cargoTypeId) &&
				!cargoTypes.map(x => x.id).includes(wizardData.innerWizardData.cargoTypeId)
			) {
				const newData = new BookingWizardData({ ...wizardData });
				if (newData.innerWizardData instanceof VehicleBookingWizardData) {
					newData.innerWizardData.cargoTypeId = '';
					newData.innerWizardData.cargoMake = '';
					newData.innerWizardData.cargoModel = '';
					newData.innerWizardData.vehicleLengthId = '';
					newData.innerWizardData.vehicleWeightId = '';
					newData.innerWizardData.vehicleWidthId = '';
					newData.disabledVehicleType = true;
					setWizardData(newData);
				}
			}
		}
	}, [cargoTypes]);

	// Moves the selected length off a measurement the operator's visibility rules no longer allow —
	// the "customer switches the driver to a senior after picking a length" case.
	useResetDisallowedMeasurements();

	if (!isVehicleBookingWizardData(wizardData.innerWizardData)) {
		return <></>;
	}

	const showRestOfInputs =
		(stringNotEmpty(wizardData.innerWizardData.cargoMake) && previousCargoList.length > 0) ||
		previousCargoList.length === 0 ||
		!appConfigStore.previousVehicleSelectionEnabled;

	const showPreviousCargo = !showRestOfInputs && !isGuest && !isLoading;

	// Show the "use a saved vehicle" action in both CREATE and ALTERATION modes. In alteration the
	// existing vehicle is pre-filled (so showRestOfInputs is true and the PreviousCargo library is
	// hidden); this button is the way back to the saved-vehicle library, so gating it to CREATE left
	// altering customers unable to pick a previous vehicle.
	const showUseSavedVehicleAction =
		!isGuest &&
		!isLoading &&
		showRestOfInputs &&
		previousCargoList.length > 0 &&
		appConfigStore.previousVehicleSelectionEnabled;

	const useSavedVehicle = action(() => {
		const cleared = wizardDataVehicleTabReducer(wizardData, VehicleTabActionCreators.clearDetails());
		if (cleared && isVehicleBookingWizardData(cleared.innerWizardData)) {
			cleared.innerWizardData.cargoIdentification = '';
			cleared.innerWizardData.cargoCharacteristics = [];
			setWizardData(cleared);
		}
		previousCargo.previous = false;
		setInnerWizardErrors({});
	});

	const showTrailerSection = appConfigStore.trailersEnabled;
	const showTrailerSubInputs = wizardData.innerWizardData.trailerTypeId !== 'NO_TRAILER';
	const trailerOptedIn =
		wizardData.innerWizardData.trailerCheckboxSelected || wizardData.innerWizardData.trailerTypeId !== 'NO_TRAILER';

	const addTrailer = () => {
		const newData = new BookingWizardData({ ...wizardData });
		if (newData.innerWizardData instanceof VehicleBookingWizardData) {
			newData.innerWizardData.trailerCheckboxSelected = true;
			newData.innerWizardData.trailerTypeId = '';
		}
		setWizardData(newData);
	};

	const applyTrailerRemoval = () => {
		const newData = new BookingWizardData({ ...wizardData });
		if (newData.innerWizardData instanceof VehicleBookingWizardData) {
			newData.innerWizardData.trailerCheckboxSelected = false;
			newData.innerWizardData.trailerTypeId = 'NO_TRAILER';
			newData.innerWizardData.trailerLengthId = '';
			newData.innerWizardData.trailerWeightId = '';
			newData.innerWizardData.trailerExactWeight = null;
			newData.innerWizardData.trailerExactWeightUnit = null;
			newData.innerWizardData.trailerExactWeightUserEdited = false;
			// Mirror the NO_TRAILER dropdown path: clear the trailer's width and characteristics
			// so removing the trailer doesn't leave stale state to resurface if it's re-added.
			newData.innerWizardData.trailerWidthId = '';
			newData.innerWizardData.showTrailerWidthDetails = false;
			newData.innerWizardData.trailerCargoCharacteristics = [];
		}
		setWizardData(newData);

		const newErrors = { ...innerWizardErrors } as Record<string, unknown>;
		delete newErrors.trailerTypeId;
		delete newErrors.trailerLengthId;
		delete newErrors.trailerExactWeight;
		setInnerWizardErrors(newErrors as typeof innerWizardErrors);
	};

	const removeTrailer = () => {
		const oldHadTrailer =
			isVehicleBookingWizardData(oldWizardData?.innerWizardData) &&
			oldWizardData!.innerWizardData.trailerTypeId !== 'NO_TRAILER';

		const needsConfirmation =
			wizardData.wizardMode === 'ALTERATION' &&
			oldHadTrailer &&
			appConfigStore.trailerRemovalFeeEnabled &&
			isNotNullOrUndefined(wizardData.departingCancellationFee) &&
			wizardData.departingCancellationFee > 0 &&
			isNotNullOrUndefined(wizardData.departingCancellationCutoffHours);

		if (needsConfirmation) {
			RenderRemoveTrailerModal(
				wizardData.departureTrip === undefined,
				wizardData.bookingToEdit ?? '',
				wizardData.departingCancellationCutoffHours!,
			).then(confirmed => {
				if (confirmed) {
					applyTrailerRemoval();
				}
				(document.activeElement as HTMLElement | null)?.blur();
			});
			return;
		}

		applyTrailerRemoval();
	};

	// Admin-configurable banner shown at the top of the vehicle tab. The text is driven by the
	// language/translation system; the banner is hidden whenever the translation is empty.
	// Resolved here only to decide whether to render — the text itself goes through FormattedMessage
	// so the translation can emphasise part of the sentence with <strong> tags.
	const vehicleTabBannerText = getMessageByKey('vehicle-tab__banner__text', '') ?? '';

	return (
		<div className="flex flex-col gap-3">
			{stringNotEmpty(vehicleTabBannerText.trim()) && (
				<MessageBox status="info" slim noBottomMargin>
					{
						<p>
							<FormattedMessage
								id="vehicle-tab__banner__text"
								defaultMessage=""
								values={{ strong: (chunks: ReactNode) => <strong>{chunks}</strong> }}
							/>
						</p>
					}
				</MessageBox>
			)}
			{showPreviousCargo && (
				<>
					<PreviousCargo
						wizardData={wizardData}
						previousCargos={previousCargoList}
						previousCargo={previousCargo}
						saveChanges={setWizardData}
						setPreviousCargoList={setPreviousCargoList}
						refetchVehicleTabData={refetchVehicleTabData}
						userId={wizardData.userId}
						cargoTypes={cargoTypes}
						setInnerWizardErrors={setInnerWizardErrors}
					/>
					<div className="booking-section-or-divider" role="separator" aria-label="or">
						<span className="booking-section-or-divider__line" />
						<span className="booking-section-or-divider__label">OR</span>
						<span className="booking-section-or-divider__line" />
					</div>
				</>
			)}
			<section className="vehicle-details-section booking-section-card">
				{(() => {
					const title = previousCargo.previous
						? intl.formatMessage({
								id: 'vehicle-tab__vehicle-section__title__saved',
								defaultMessage: 'Your saved vehicle',
							})
						: intl.formatMessage({
								id: 'vehicle-tab__vehicle-section__title',
								defaultMessage: 'Add a new vehicle',
							});
					let descriptionDefault: string;
					if (previousCargo.previous) {
						descriptionDefault = 'Confirm or update the details below.';
					} else if (!showRestOfInputs) {
						descriptionDefault = 'Enter the details of a new vehicle below.';
					} else {
						descriptionDefault = "Tell us about the vehicle you're bringing aboard.";
					}
					const description = fmtOptional({
						id: previousCargo.previous
							? 'vehicle-tab__vehicle-section__description__saved'
							: 'vehicle-tab__vehicle-section__description',
						defaultMessage: descriptionDefault,
					});
					const useSavedVehicleLabel = previousCargo.previous
						? intl.formatMessage({
								id: 'vehicle-tab__use-different-saved-vehicle__label',
								defaultMessage: 'Use a different saved vehicle',
							})
						: intl.formatMessage({
								id: 'vehicle-tab__use-saved-vehicle__label',
								defaultMessage: 'Use a saved vehicle',
							});
					return (
						<header
							className={`booking-section-header${showUseSavedVehicleAction ? ' booking-section-header--with-action' : ''}`}
						>
							<span className="booking-section-heading__icon">
								<Icon name="directions_car" useMaterialIcons />
							</span>
							<div className="booking-section-header__text">
								<h4 className="booking-section-title">{title}</h4>
								{description && <p className="booking-section-description">{description}</p>}
							</div>
							{showUseSavedVehicleAction && (
								<button
									type="button"
									className="booking-section-action-button"
									onClick={useSavedVehicle}
								>
									<Icon name="history" useMaterialIcons />
									<span>{useSavedVehicleLabel}</span>
								</button>
							)}
						</header>
					);
				})()}
				<div className="vehicle-details-body grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
					<VehicleMakeInput />
					{showRestOfInputs && (
						<>
							<VehicleModelInput />
							<VehicleRegoInput />
							<If condition={appConfigStore.vehicleLengthEnabled}>
								<VehicleLengthInput />
							</If>
							<VehicleExactWeightInput />
							<If condition={appConfigStore.vehicleWidthEnabled}>
								<VehicleWidthDetails />
							</If>
							<VehicleCargoCharacteristicDetails />
						</>
					)}
				</div>
			</section>
			{showRestOfInputs &&
				showTrailerSection &&
				(trailerOptedIn ? (
					<section className="vehicle-details-section trailer-details-section booking-section-card">
						{(() => {
							const description = fmtOptional({
								id: 'vehicle-tab__trailer-section__description',
								defaultMessage: "Tell us about the trailer you'll be towing.",
							});
							return (
								<header className="booking-section-header booking-section-header--with-action">
									<span className="booking-section-heading__icon">
										<Icon name="rv_hookup" useMaterialIcons />
									</span>
									<div className="booking-section-header__text">
										<h4 className="booking-section-title">
											{intl.formatMessage({
												id: 'vehicle-tab__trailer-section__title',
												defaultMessage: 'Trailer details',
											})}
										</h4>
										{description && <p className="booking-section-description">{description}</p>}
									</div>
									<button
										type="button"
										className="booking-section-remove-button"
										onClick={removeTrailer}
									>
										<Icon name="close" useMaterialIcons />
										<span>
											{intl.formatMessage({
												id: 'vehicle-tab__remove-trailer__label',
												defaultMessage: 'Remove',
											})}
										</span>
									</button>
								</header>
							);
						})()}
						<div className="vehicle-details-body grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
							<TrailerTypeInput />
							{showTrailerSubInputs && (
								<>
									<TrailerLengthInput />
									<TrailerExactWeightInput />
									<TrailerCargoCharacteristicDetails />
									<If condition={appConfigStore.vehicleWidthEnabled}>
										<TrailerWidthDetails />
									</If>
								</>
							)}
						</div>
					</section>
				) : (
					<button type="button" className="booking-section-add-button" onClick={addTrailer}>
						<span className="booking-section-heading__icon">
							<Icon name="add" useMaterialIcons />
						</span>
						{intl.formatMessage({
							id: 'vehicle-tab__add-trailer__label',
							defaultMessage: 'Add a trailer',
						})}
					</button>
				))}
			<VehicleDriverDetailsForm />

			<DynamicFieldsSection
				bookingType="VEHICLE"
				subjectUserDisplayNameId={subjectUserDisplayNameId}
				values={(wizardData.innerWizardData as VehicleBookingWizardData).dynamicFieldValues ?? {}}
				errors={(innerWizardErrors as any)?.dynamicFieldValues as Record<string, string> | undefined}
				onChange={next => {
					const newData = new BookingWizardData({ ...wizardData });
					if (isVehicleBookingWizardData(newData.innerWizardData)) {
						newData.innerWizardData.dynamicFieldValues = next;
					}
					setWizardData(newData);
					// Clear the dynamic-fields error bucket as the user starts typing —
					// validation re-runs on next-step click and rebuilds it.
					if ((innerWizardErrors as any)?.dynamicFieldValues) {
						const cleared = { ...(innerWizardErrors as any) };
						delete cleared.dynamicFieldValues;
						setInnerWizardErrors(cleared);
					}
				}}
				noteField={{
					enabled: appConfigStore.vehicleNoteEnabled,
					required: appConfigStore.vehicleNoteRequired,
					label:
						getMessageByKey('vehicle-tab__additional-information__label', 'Additional information') ??
						'Additional information',
					placeholder:
						getMessageByKey(
							'vehicle-tab__additional-information__placeholder',
							'Enter additional information',
						) ?? undefined,
					value: (wizardData.innerWizardData as VehicleBookingWizardData).note ?? '',
					error: (innerWizardErrors as any)?.note,
					onChange: next => {
						const newData = new BookingWizardData({ ...wizardData });
						if (isVehicleBookingWizardData(newData.innerWizardData)) {
							newData.innerWizardData.note = next;
						}
						setWizardData(newData);
						if ((innerWizardErrors as any)?.note) {
							const cleared = { ...(innerWizardErrors as any) };
							delete cleared.note;
							setInnerWizardErrors(cleared);
						}
					},
				}}
			/>
		</div>
	);
}
